import { useCallback, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, Quote, X, ChevronLeft, ChevronRight } from "lucide-react";

const A = "https://customer-assets-jai6qajn.emergentagent.net/job_adrien-sebert-dj/artifacts/";

const COUPLE = {
  names: "Morgane & Melissa",
  place: "Normandie",
  quote:
    "Tout était absolument parfait du début à la fin. Une ambiance incroyable, tout le monde a dansé jusqu'au bout de la nuit. Merci Adrien d'avoir rendu cette journée inoubliable.",
};

const CHAPTERS = [
  { step: "01", phase: "Avant", title: "Tout est prêt", text: "Son, lumières, machines à effets : installation millimétrée avant l'arrivée des invités.", src: `${A}6hin5qe7_IMG_5390.webp` },
  { step: "★", phase: "Moment fort", title: "L'ouverture de bal", text: "Fumée lourde, lumières roses et un premier slow suspendu dans le temps.", src: `${A}hnmrct6s_IMG_7546.jpeg`, hero: true },
  { step: "02", phase: "Pendant", title: "La pièce montée", text: "Canons à étincelles et CO2 pour un dessert digne d'un show.", src: `${A}85i4fjvw_IMG_7549.webp` },
  { step: "03", phase: "Pendant", title: "Le dancefloor", text: "Toutes les générations mains en l'air, sous les poutres de la grange.", src: `${A}pmri3orq_IMG_5389.webp` },
  { step: "04", phase: "Après", title: "Le dernier slow", text: "Les mariés dans les bras l'un de l'autre, entourés de leurs proches. Mission accomplie.", src: `${A}dqbzmd26_IMG_7548.webp` },
];

const Viewer = ({ index, onClose, onPrev, onNext }) => {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") onPrev();
      if (e.key === "ArrowRight") onNext();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [onClose, onPrev, onNext]);
  const c = CHAPTERS[index];
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      data-testid="wedding-lightbox"
      className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-xl flex items-center justify-center p-4 md:p-10"
      onClick={onClose}
    >
      <button onClick={onClose} aria-label="Fermer" data-testid="wedding-lightbox-close" className="absolute top-4 right-4 md:top-6 md:right-6 w-11 h-11 rounded-full bg-white/10 hover:bg-[#D4AF37] hover:text-black text-white flex items-center justify-center transition-colors">
        <X size={20} />
      </button>
      <button onClick={(e) => { e.stopPropagation(); onPrev(); }} aria-label="Précédent" data-testid="wedding-lightbox-prev" className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-[#D4AF37] hover:text-black text-white flex items-center justify-center transition-colors">
        <ChevronLeft size={22} />
      </button>
      <button onClick={(e) => { e.stopPropagation(); onNext(); }} aria-label="Suivant" data-testid="wedding-lightbox-next" className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/10 hover:bg-[#D4AF37] hover:text-black text-white flex items-center justify-center transition-colors">
        <ChevronRight size={22} />
      </button>
      <motion.figure key={index} initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3 }} className="max-w-6xl w-full flex flex-col items-center" onClick={(e) => e.stopPropagation()}>
        <img src={c.src} alt={c.title} data-testid="wedding-lightbox-image" className="max-h-[78vh] w-auto max-w-full object-contain rounded-2xl border border-white/10 shadow-2xl" />
        <figcaption className="mt-4 flex items-center gap-3 text-sm">
          <span className="text-[10px] tracking-[0.28em] uppercase text-[#D4AF37]">{c.step} · {c.phase}</span>
          <span className="text-white">{c.title}</span>
        </figcaption>
      </motion.figure>
    </motion.div>
  );
};

export const FeaturedWedding = () => {
  const [active, setActive] = useState(null);
  const close = useCallback(() => setActive(null), []);
  const prev = useCallback(() => setActive((i) => (i - 1 + CHAPTERS.length) % CHAPTERS.length), []);
  const next = useCallback(() => setActive((i) => (i + 1) % CHAPTERS.length), []);

  return (
    <section
      id="mariage-vedette"
      data-testid="featured-wedding-section"
      className="relative py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-black border-t border-white/5 overflow-hidden"
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_15%_25%,rgba(255,45,156,0.14),transparent_55%),radial-gradient(ellipse_at_85%_80%,rgba(212,175,55,0.12),transparent_55%)]" />

      <div className="relative z-10 max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl mb-12"
        >
          <span className="eyebrow inline-flex items-center gap-2">
            <Heart size={13} /> Mariage vedette · Avant, pendant, après
          </span>
          <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
            {COUPLE.names}. <span className="gold-text-gradient">Une soirée racontée de l'intérieur.</span>
          </h2>
          <p className="mt-5 text-zinc-400 leading-relaxed">
            De l'installation au dernier slow : voici comment se déroule un mariage
            avec DJ AS Animation. Une seule équipe, une organisation millimétrée,
            zéro stress pour les mariés.
          </p>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-4 auto-rows-[170px] md:auto-rows-[215px] gap-3 md:gap-4">
          {CHAPTERS.map((c, i) => (
            <motion.button
              key={c.step}
              type="button"
              onClick={() => setActive(i)}
              data-testid={`wedding-chapter-${i}`}
              aria-label={`${c.phase} – ${c.title}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.55, delay: i * 0.07 }}
              className={`group relative overflow-hidden rounded-2xl border border-white/10 hover:border-[#D4AF37]/50 text-left ${
                c.hero ? "col-span-2 row-span-2 order-first" : ""
              }`}
            >
              <img src={c.src} alt={c.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
              <div className="absolute top-3 left-3 inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur text-[10px] tracking-[0.22em] uppercase">
                <span className="text-[#D4AF37] font-semibold">{c.step}</span>
                <span className="text-zinc-300">{c.phase}</span>
              </div>
              <div className="absolute bottom-3 left-3 right-3 md:bottom-4 md:left-4 md:right-4">
                <div className={`text-white font-semibold leading-tight ${c.hero ? "text-lg md:text-2xl" : "text-sm md:text-base"}`}>{c.title}</div>
                <div className={`mt-1 text-zinc-300 leading-snug ${c.hero ? "text-xs md:text-sm" : "text-[11px] md:text-xs line-clamp-2"}`}>{c.text}</div>
              </div>
            </motion.button>
          ))}

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.55, delay: 0.4 }}
            data-testid="wedding-quote"
            className="col-span-2 md:col-span-4 relative rounded-2xl glass border border-[#D4AF37]/25 p-5 md:p-7 flex flex-col md:flex-row md:items-center justify-between gap-4 md:gap-10 overflow-hidden"
          >
            <Quote size={64} className="absolute -top-3 -right-2 text-[#D4AF37]/10" />
            <p className="text-zinc-100 leading-relaxed text-sm md:text-lg italic md:flex-1">« {COUPLE.quote} »</p>
            <div className="flex items-end md:items-center justify-between md:justify-end gap-3 md:gap-6 md:shrink-0">
              <div>
                <div className="text-white font-semibold text-sm">{COUPLE.names}</div>
                <div className="text-[10px] tracking-[0.22em] uppercase text-zinc-400 mt-0.5">Mariés · {COUPLE.place}</div>
              </div>
              <a href="#contact" data-testid="wedding-cta" className="btn-gold rounded-full px-4 py-2 text-xs whitespace-nowrap">
                Vérifier ma date
              </a>
            </div>
          </motion.div>
        </div>
      </div>

      <AnimatePresence>
        {active !== null && <Viewer index={active} onClose={close} onPrev={prev} onNext={next} />}
      </AnimatePresence>
    </section>
  );
};
